import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useClaimsToggle } from '@npm-client-frontends/shared-hooks'
import { AudienceGroups } from '@npm-client-frontends/client-frontends-shared-types'
import { Fragment, useState } from 'react'
import { ContentSet } from './Header'

export default ({
  userAudienceGroups,
  contentSets,
}: {
  userAudienceGroups: AudienceGroups[]
  contentSets: ContentSet[]
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const [warningDialogEntity, setWarningDialogEntity] = useState<string>()

  const { selectedClaims, toggleClaims, setClaims } = useClaimsToggle()

  const handleWarningDialogClose = () => setWarningDialogEntity(undefined)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <>
      <Box>
        <>
          <Box
            sx={{
              display: 'flex',
              marginLeft: 5.5,
              backgroundColor: '#e0e0e0',
              borderRadius: 1,
              padding: '0 5px 0 10px',
              height: '45px',
              alignItems: 'center',
              gap: '2px',
            }}
          >
            {contentSets.map(({ audienceGroup, color, title }, index) => {
              const hasAccess = userAudienceGroups.includes(audienceGroup)
              const isActive = selectedClaims.includes(audienceGroup)
              const isLastItem = index === contentSets.length - 1
              return (
                <Tooltip title={title} enterDelay={500} key={audienceGroup}>
                  <Button
                    disabled={!hasAccess}
                    onClick={() => {
                      toggleClaims(audienceGroup)
                    }}
                    sx={{
                      marginBottom: '3px',
                      width: 32,
                      height: 28,
                      bgcolor: color,
                      marginRight: isLastItem ? 0 : 0.8,
                      minWidth: 'unset',
                      padding: 0,
                      color: 'white',
                      fontSize: 12,
                      borderRadius: '3px',
                      fontWeight: isActive ? 900 : 400,
                      boxShadow: isActive ? '0 0 0 2px #303c64' : '',
                      lineHeight: '28px',
                      position: 'relative',
                      '&:hover': {
                        color: 'white',
                        bgcolor: `${color}80`,
                      },
                      '&::after': isActive
                        ? {
                            content: '""',
                            position: 'absolute',
                            bottom: -8,
                            left: 0,
                            width: '100%',
                            height: '4px',
                            backgroundColor: color,
                          }
                        : {},
                      '&.Mui-disabled': {
                        backgroundColor: color,
                        opacity: 0.5,
                        cursor: 'not-allowed',
                        pointerEvents: 'auto',
                      },
                    }}
                  >
                    {userAudienceGroups && userAudienceGroups.length > 0 ? (
                      hasAccess ? (
                        audienceGroup
                      ) : (
                        <LockIcon
                          sx={{
                            fontSize: '16px',
                            color: 'white',
                            display: 'flex',
                          }}
                        />
                      )
                    ) : null}
                  </Button>
                </Tooltip>
              )
            })}
            <Button
              onClick={handleClick}
              sx={{
                minWidth: 'unset',
                padding: '4px',
                height: 28,
                marginLeft: 0.8,
                color: '#666',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </Button>
          </Box>
          <Menu
            id="claims-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            MenuListProps={{
              onMouseLeave: handleClose,
            }}
          >
            <MenuItem
              sx={{
                borderBottom: '1px solid #e0e0e0',
                pointerEvents: 'none',
                fontSize: '14px',
                color: '#666',
                height: '20px',
                fontWeight: 'bold',
              }}
            >
              {selectedClaims.length} / {contentSets.length} content sets
              selected
            </MenuItem>
            {contentSets.map(({ audienceGroup, title, color }) => {
              const hasAccess = userAudienceGroups.includes(audienceGroup)
              const isActive = selectedClaims.includes(audienceGroup)

              const menuItemElem = (
                <MenuItem
                  disableRipple={!hasAccess}
                  sx={{
                    backgroundColor: isActive ? '#a6b6d7' : 'inherit',
                    fontSize: '14px',
                    color: '#384fa1',
                    fontWeight: isActive ? 900 : 400,
                    display: 'flex',
                    justifyContent: 'space-between',
                    cursor: hasAccess ? 'pointer' : 'not-allowed',
                    alignItems: 'center',
                    '&:hover': {
                      backgroundColor: isActive
                        ? '#a6b6d7'
                        : 'rgba(0, 0, 0, 0.08)',
                      opacity: isActive ? 1 : 0.8,
                    },
                  }}
                  onClick={() => {
                    toggleClaims(audienceGroup)
                  }}
                >
                  <Box
                    sx={{
                      flexGrow: 1,
                      opacity: hasAccess ? 1 : 0.5,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 28,
                        bgcolor: color,
                        display: 'inline-block',
                        marginRight: 1,
                        color: 'white',
                        fontSize: 12,
                        borderRadius: '3px',
                        textAlign: 'center',
                        fontWeight: isActive ? 900 : 400,
                        lineHeight: '28px',
                        position: 'relative',
                      }}
                    >
                      {hasAccess ? (
                        audienceGroup
                      ) : (
                        <LockIcon
                          sx={{
                            fontSize: '16px',
                            color: 'white',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                          }}
                        />
                      )}
                    </Box>
                    {title}
                  </Box>
                  <Box
                    sx={{
                      width: '1px',
                      height: '24px',
                      backgroundColor: '#e0e0e0',
                      margin: '0 -5px 0 20px',
                    }}
                  />
                  <Button
                    disabled={!hasAccess}
                    onClick={(e) => {
                      e.stopPropagation()
                      setClaims([audienceGroup])
                    }}
                    size="small"
                    variant="contained"
                    sx={{
                      minWidth: 'auto',
                      ml: 2,
                      fontSize: '10px',
                      backgroundColor: '#384fa1',
                      color: 'white',
                      boxShadow: 'none',
                      padding: '2px 8px',
                      pointerEvents: 'auto',
                      '&:hover': {
                        backgroundColor: '#5c74c7',
                        boxShadow: 'none',
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        color: 'rgba(0, 0, 0, 0.26)',
                      },
                    }}
                  >
                    Only
                  </Button>
                </MenuItem>
              )

              return hasAccess ? (
                <Fragment key={audienceGroup}>{menuItemElem}</Fragment>
              ) : (
                <Tooltip
                  key={audienceGroup}
                  title="Your subscription does not include access to this content set. For access, contact your account manager or help@newprojectmedia.com."
                  followCursor
                  enterDelay={500}
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, -70],
                          },
                        },
                      ],
                    },
                  }}
                >
                  {menuItemElem}
                </Tooltip>
              )
            })}
          </Menu>
        </>
      </Box>
      {warningDialogEntity && (
        <Dialog open onClose={handleWarningDialogClose}>
          <DialogTitle>
            {
              contentSets.find(
                (detail) => detail.audienceGroup === warningDialogEntity,
              )?.title
            }
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {`Your subscription does not include access to ${
                contentSets.find(
                  (detail) => detail.audienceGroup === warningDialogEntity,
                )?.title
              } (${contentSets.find((detail) => detail.audienceGroup === warningDialogEntity)?.abbreviation}) content. For access, contact your account manager or `}
              <a href="mailto:help@newprojectmedia.com">
                help@newprojectmedia.com
              </a>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleWarningDialogClose} autoFocus>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}
